/**
 * Die VBA-Prüfmakros des PowerPoint-Kurses.
 *
 * PowerPoint ist von Office.js am schwächsten abgedeckt. Im gesamten
 * PowerPoint-Namespace fehlen Notizen, Übergänge, Animationen, Abschnitte,
 * Kommentare, ausgeblendete Folien, Diagrammtyp, Bildzuschnitt, die
 * Einstellungen der Bildschirmpräsentation, Aktionseinstellungen, die
 * Sprache von Text sowie Notizen- und Handzettelmaster. Genau diese Angaben
 * lesen die Makros hier aus.
 */

import { pruefmakro } from "@/office/makro/vba";

const WOERTER_ZAEHLEN = `
Private Function WoerterZaehlen(ByVal s As String) As Long
    Dim teile() As String, i As Long
    s = Replace(Replace(Replace(s, vbCr, " "), vbLf, " "), Chr$(11), " ")
    s = Trim$(s)
    If Len(s) = 0 Then Exit Function
    teile = Split(s, " ")
    For i = LBound(teile) To UBound(teile)
        If Len(teile(i)) > 0 Then WoerterZaehlen = WoerterZaehlen + 1
    Next i
End Function`;

/** Sprechernotizen jeder Folie. */
export const MAKRO_NOTIZEN = pruefmakro({
  pruefung: "pp-notizen",
  host: "PowerPoint",
  titel: "Sprechernotizen auslesen",
  rumpf: [
    "Dim folie As Slide, form As Shape, n As Long, notiz As String",
    "For Each folie In ActivePresentation.Slides",
    "    n = n + 1",
    '    notiz = ""',
    "    For Each form In folie.NotesPage.Shapes",
    "        If form.Type = msoPlaceholder Then",
    "            If form.PlaceholderFormat.Type = ppPlaceholderBody Then",
    "                If form.HasTextFrame Then notiz = form.TextFrame.TextRange.Text",
    "            End If",
    "        End If",
    "    Next form",
    '    Zahl "folie." & n & ".woerter", WoerterZaehlen(notiz)',
    '    Wert "folie." & n & ".anfang", Left$(notiz, 80)',
    "Next folie",
    'Zahl "folien", n',
  ],
  hilfsfunktionen: [WOERTER_ZAEHLEN],
});

/** Folienübergänge und Weiterschaltung jeder Folie. */
export const MAKRO_UEBERGAENGE = pruefmakro({
  pruefung: "pp-uebergaenge",
  host: "PowerPoint",
  titel: "Folienübergänge auslesen",
  rumpf: [
    "Dim folie As Slide, n As Long",
    "For Each folie In ActivePresentation.Slides",
    "    n = n + 1",
    "    With folie.SlideShowTransition",
    '        Zahl "folie." & n & ".effekt", .EntryEffect',
    '        Zahl "folie." & n & ".dauer", .Duration',
    '        JaNein "folie." & n & ".klick", .AdvanceOnClick',
    '        JaNein "folie." & n & ".zeit", .AdvanceOnTime',
    '        Zahl "folie." & n & ".sekunden", .AdvanceTime',
    '        JaNein "folie." & n & ".ausgeblendet", .Hidden',
    "    End With",
    "Next folie",
    'Zahl "folien", n',
  ],
});

/** Animationen jeder Folie aus der Hauptsequenz. */
export const MAKRO_ANIMATIONEN = pruefmakro({
  pruefung: "pp-animationen",
  host: "PowerPoint",
  titel: "Animationen auslesen",
  rumpf: [
    "Dim folie As Slide, effekt As Effect, n As Long, i As Long",
    "Dim ausgang As Long, nachAbsatz As Long, klick As Long, mitVorher As Long, nachVorher As Long",
    "For Each folie In ActivePresentation.Slides",
    "    n = n + 1",
    "    ausgang = 0",
    "    nachAbsatz = 0",
    "    klick = 0",
    "    mitVorher = 0",
    "    nachVorher = 0",
    "    For i = 1 To folie.TimeLine.MainSequence.Count",
    "        Set effekt = folie.TimeLine.MainSequence(i)",
    "        If effekt.Exit Then ausgang = ausgang + 1",
    "        If effekt.Paragraph > 0 Then nachAbsatz = nachAbsatz + 1",
    "        Select Case effekt.Timing.TriggerType",
    "            Case msoAnimTriggerOnPageClick",
    "                klick = klick + 1",
    "            Case msoAnimTriggerWithPrevious",
    "                mitVorher = mitVorher + 1",
    "            Case msoAnimTriggerAfterPrevious",
    "                nachVorher = nachVorher + 1",
    "        End Select",
    "    Next i",
    '    Zahl "folie." & n & ".effekte", folie.TimeLine.MainSequence.Count',
    '    Zahl "folie." & n & ".ausgang", ausgang',
    '    Zahl "folie." & n & ".nachAbsatz", nachAbsatz',
    '    Zahl "folie." & n & ".beimKlicken", klick',
    '    Zahl "folie." & n & ".mitVorheriger", mitVorher',
    '    Zahl "folie." & n & ".nachVorheriger", nachVorher',
    "Next folie",
    'Zahl "folien", n',
  ],
});

/** Abschnitte der Präsentation. */
export const MAKRO_ABSCHNITTE = pruefmakro({
  pruefung: "pp-abschnitte",
  host: "PowerPoint",
  titel: "Abschnitte auslesen",
  rumpf: [
    "Dim i As Long",
    "With ActivePresentation.SectionProperties",
    '    Zahl "abschnitte", .Count',
    "    For i = 1 To .Count",
    '        Wert "abschnitt." & i & ".name", .Name(i)',
    '        Zahl "abschnitt." & i & ".ersteFolie", .FirstSlide(i)',
    '        Zahl "abschnitt." & i & ".folien", .SlidesCount(i)',
    "    Next i",
    "End With",
    'Zahl "folien", ActivePresentation.Slides.Count',
  ],
});

/** Diagramme: Typ, Titel, Legende, Datenbeschriftungen. */
export const MAKRO_DIAGRAMME = pruefmakro({
  pruefung: "pp-diagramme",
  host: "PowerPoint",
  titel: "Diagramme auslesen",
  rumpf: [
    "Dim folie As Slide, form As Shape, n As Long, beschriftet As Boolean",
    "For Each folie In ActivePresentation.Slides",
    "    For Each form In folie.Shapes",
    "        If form.HasChart Then",
    "            n = n + 1",
    '            Zahl "diagramm." & n & ".folie", folie.SlideIndex',
    '            Zahl "diagramm." & n & ".typ", form.Chart.ChartType',
    '            JaNein "diagramm." & n & ".titel", form.Chart.HasTitle',
    '            If form.Chart.HasTitle Then Wert "diagramm." & n & ".titeltext", form.Chart.ChartTitle.Text',
    '            JaNein "diagramm." & n & ".legende", form.Chart.HasLegend',
    "            beschriftet = False",
    "            On Error Resume Next",
    "            beschriftet = form.Chart.SeriesCollection(1).HasDataLabels",
    "            On Error GoTo Fehler",
    '            JaNein "diagramm." & n & ".beschriftung", beschriftet',
    "        End If",
    "    Next form",
    "Next folie",
    'Zahl "diagramme", n',
  ],
});

/** Diagrammtypen mit Tiefenwirkung (xl3D…, Zylinder, Kegel, Pyramide). */
export function istRaeumlich(typ: number): boolean {
  return [-4100, 54, 55, 56, 60, 61, 62, -4101, -4102, 70, -4098, 78, 79].includes(typ) || (typ >= 92 && typ <= 112);
}

/** Gängige Diagrammtypen für die Rückmeldung. */
export const DIAGRAMMTYP: Record<number, string> = {
  51: "Gruppierte Säulen",
  52: "Gestapelte Säulen",
  57: "Gruppierte Balken",
  58: "Gestapelte Balken",
  4: "Linie",
  65: "Linie mit Datenpunkten",
  5: "Kreis",
  [-4120]: "Ring",
  [-4169]: "Punkt (XY)",
  1: "Fläche",
};

/** Bilder: Zuschnitt und gesperrtes Seitenverhältnis. */
export const MAKRO_BILDER = pruefmakro({
  pruefung: "pp-bilder",
  host: "PowerPoint",
  titel: "Bilder auslesen",
  rumpf: [
    "Dim folie As Slide, form As Shape, istBild As Boolean",
    "Dim bilder As Long, zugeschnitten As Long, gesperrt As Long",
    "For Each folie In ActivePresentation.Slides",
    "    For Each form In folie.Shapes",
    "        istBild = (form.Type = msoPicture)",
    "        If form.Type = msoPlaceholder Then",
    "            If form.PlaceholderFormat.ContainedType = msoPicture Then istBild = True",
    "        End If",
    "        If istBild Then",
    "            bilder = bilder + 1",
    "            With form.PictureFormat",
    "                If .CropLeft + .CropRight + .CropTop + .CropBottom > 0.5 Then zugeschnitten = zugeschnitten + 1",
    "            End With",
    "            If form.LockAspectRatio Then gesperrt = gesperrt + 1",
    "        End If",
    "    Next form",
    "Next folie",
    'Zahl "bilder", bilder',
    'Zahl "zugeschnitten", zugeschnitten',
    'Zahl "seitenverhaeltnisGesperrt", gesperrt',
  ],
});

/** Bildschirmpräsentation einrichten, Zielgruppen, ausgeblendete Folien, Anzeigedauern. */
export const MAKRO_BILDSCHIRMPRAESENTATION = pruefmakro({
  pruefung: "pp-einrichten",
  host: "PowerPoint",
  titel: "Bildschirmpräsentation auslesen",
  rumpf: [
    "Dim i As Long, folie As Slide, ausgeblendet As Long, mitZeit As Long, referent As Boolean",
    "With ActivePresentation.SlideShowSettings",
    '    Zahl "art", .ShowType',
    '    JaNein "endlos", .LoopUntilStopped',
    '    Zahl "weiter", .AdvanceMode',
    '    Zahl "zielgruppen", .NamedSlideShows.Count',
    "    For i = 1 To .NamedSlideShows.Count",
    '        Wert "zielgruppe." & i & ".name", .NamedSlideShows(i).Name',
    '        Zahl "zielgruppe." & i & ".folien", .NamedSlideShows(i).Count',
    "    Next i",
    "    ' Die Referentenansicht kennt erst PowerPoint 2013.",
    "    On Error Resume Next",
    "    referent = .ShowPresenterView",
    "    On Error GoTo Fehler",
    '    JaNein "referentenansicht", referent',
    "End With",
    "For Each folie In ActivePresentation.Slides",
    "    If folie.SlideShowTransition.Hidden Then ausgeblendet = ausgeblendet + 1",
    "    If folie.SlideShowTransition.AdvanceOnTime Then mitZeit = mitZeit + 1",
    "Next folie",
    'Zahl "folien", ActivePresentation.Slides.Count',
    'Zahl "ausgeblendet", ausgeblendet',
    'Zahl "mitAnzeigedauer", mitZeit',
  ],
});

/** VBA-Werte für `SlideShowSettings.ShowType`. */
export const PRAESENTATIONSART: Record<number, string> = {
  1: "Präsentation durch einen Redner",
  2: "Ansicht durch ein Individuum (Fenster)",
  3: "Ansicht an einem Kiosk",
};

/** Interaktive Schaltflächen, Aktionen auf Formen und Medien. */
export const MAKRO_INTERAKTIV = pruefmakro({
  pruefung: "pp-interaktiv",
  host: "PowerPoint",
  titel: "Aktionen und Medien auslesen",
  rumpf: [
    "Dim folie As Slide, form As Shape, i As Long, effekt As Effect",
    "Dim aktionen As Long, schaltflaechen As Long, medien As Long, automatisch As Long",
    "For Each folie In ActivePresentation.Slides",
    "    For Each form In folie.Shapes",
    "        If form.ActionSettings(ppMouseClick).Action <> ppActionNone Then aktionen = aktionen + 1",
    "        If form.Type = msoAutoShape Then",
    "            If form.AutoShapeType >= msoShapeActionButtonCustom And form.AutoShapeType <= msoShapeActionButtonMovie Then",
    "                schaltflaechen = schaltflaechen + 1",
    "            End If",
    "        End If",
    "        If form.Type = msoMedia Then medien = medien + 1",
    "    Next form",
    "    For i = 1 To folie.TimeLine.MainSequence.Count",
    "        Set effekt = folie.TimeLine.MainSequence(i)",
    "        If effekt.EffectType = msoAnimEffectMediaPlay Then",
    "            If effekt.Timing.TriggerType <> msoAnimTriggerOnPageClick Then automatisch = automatisch + 1",
    "        End If",
    "    Next i",
    "Next folie",
    'Zahl "aktionen", aktionen',
    'Zahl "schaltflaechen", schaltflaechen',
    'Zahl "medien", medien',
    'Zahl "medienAutomatisch", automatisch',
  ],
});

/** Kommentare samt Antworten. */
export const MAKRO_KOMMENTARE = pruefmakro({
  pruefung: "pp-kommentare",
  host: "PowerPoint",
  titel: "Kommentare auslesen",
  rumpf: [
    "Dim folie As Slide, i As Long, kommentare As Long, antworten As Long",
    "For Each folie In ActivePresentation.Slides",
    "    kommentare = kommentare + folie.Comments.Count",
    "    For i = 1 To folie.Comments.Count",
    "        ' Antworten gibt es erst ab PowerPoint 2013.",
    "        On Error Resume Next",
    "        antworten = antworten + folie.Comments(i).Replies.Count",
    "        On Error GoTo Fehler",
    "    Next i",
    "Next folie",
    'Zahl "kommentare", kommentare',
    'Zahl "antworten", antworten',
  ],
});

/** Sprache des Textes in allen Formen. */
export const MAKRO_SPRACHE = pruefmakro({
  pruefung: "pp-sprache",
  host: "PowerPoint",
  titel: "Textsprache auslesen",
  rumpf: [
    "Dim folie As Slide, form As Shape, k As Long, n As Long, lid As Long, bekannt As Boolean",
    "Dim ids(1 To 20) As Long, anz(1 To 20) As Long",
    "For Each folie In ActivePresentation.Slides",
    "    For Each form In folie.Shapes",
    "        If form.HasTextFrame Then",
    "            If form.TextFrame.HasText Then",
    "                lid = form.TextFrame.TextRange.LanguageID",
    "                bekannt = False",
    "                For k = 1 To n",
    "                    If ids(k) = lid Then",
    "                        anz(k) = anz(k) + 1",
    "                        bekannt = True",
    "                    End If",
    "                Next k",
    "                If Not bekannt And n < 20 Then",
    "                    n = n + 1",
    "                    ids(n) = lid",
    "                    anz(n) = 1",
    "                End If",
    "            End If",
    "        End If",
    "    Next form",
    "Next folie",
    'Zahl "sprachen", n',
    "For k = 1 To n",
    '    Zahl "sprache." & k & ".id", ids(k)',
    '    Zahl "sprache." & k & ".formen", anz(k)',
    "Next k",
  ],
});

/** Notizen- und Handzettelmaster, Führungslinien. */
export const MAKRO_MASTER = pruefmakro({
  pruefung: "pp-master",
  host: "PowerPoint",
  titel: "Notizen- und Handzettelmaster auslesen",
  rumpf: [
    "If ActivePresentation.HasNotesMaster Then",
    "    With ActivePresentation.NotesMaster.HeadersFooters.Footer",
    '        JaNein "notizenFusszeile", .Visible',
    '        Wert "notizenFusszeileText", .Text',
    "    End With",
    "End If",
    "If ActivePresentation.HasHandoutMaster Then",
    "    With ActivePresentation.HandoutMaster.HeadersFooters.Footer",
    '        JaNein "handzettelFusszeile", .Visible',
    '        Wert "handzettelFusszeileText", .Text',
    "    End With",
    "End If",
    "' Führungslinien gibt es erst ab PowerPoint 2013.",
    "On Error Resume Next",
    'Zahl "fuehrungslinien", ActivePresentation.Guides.Count',
    "On Error GoTo Fehler",
  ],
});

/** Liegt neben der Präsentation eine gleichnamige PDF-Datei? */
export const MAKRO_PDF = pruefmakro({
  pruefung: "pp-pdf",
  host: "PowerPoint",
  titel: "PDF-Fassung suchen",
  rumpf: [
    "Dim basis As String, pdf As String, treffer As String",
    'If ActivePresentation.Path = "" Then',
    '    JaNein "gespeichert", False',
    "Else",
    '    JaNein "gespeichert", True',
    "    basis = ActivePresentation.FullName",
    '    basis = Left$(basis, InStrRev(basis, ".") - 1)',
    '    pdf = basis & ".pdf"',
    '    Wert "datei", pdf',
    "    ' Bei Dateien in OneDrive oder SharePoint ist der Pfad eine Webadresse.",
    "    On Error Resume Next",
    "    treffer = Dir$(pdf)",
    "    If Err.Number <> 0 Then",
    '        JaNein "pfadLesbar", False',
    "        Err.Clear",
    "    Else",
    '        JaNein "pfadLesbar", True',
    '        JaNein "pdf", Len(treffer) > 0',
    '        If Len(treffer) > 0 Then Wert "pdfZeit", Format$(FileDateTime(pdf), "yyyy-mm-dd\\Thh:nn:ss")',
    "    End If",
    "    On Error GoTo Fehler",
    "End If",
  ],
});
